'use client'

import posthog, { PostHog } from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, ReactNode } from 'react'

// Custom event for cookie consent ready
const COOKIE_CONSENT_READY = 'cookieConsentReady';

let posthogInstance: PostHog | null = null

function initPostHog(): PostHog | null {
  if (typeof window !== 'undefined' && !posthogInstance) {
    const consent = window.CookieConsent?.acceptedCategory('analytics')
    if (consent) {
      const instance = posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        person_profiles: 'identified_only',
        loaded: (posthog) => {
          if (!window.CookieConsent?.acceptedCategory('analytics')) {
            posthog.opt_out_capturing()
          }
        },
        autocapture: false,
        disable_session_recording: true,
        disable_persistence: true
      })
      if (instance) {
        posthogInstance = instance
      }
    }
  }
  return posthogInstance
}

function enablePostHog(): void {
  if (!posthogInstance) {
    posthogInstance = initPostHog()
  }
  if (posthogInstance) {
    posthogInstance.opt_in_capturing()
    // Only enable features after consent
    posthogInstance.config.disable_session_recording = false
    posthogInstance.config.disable_persistence = false
    posthogInstance.config.autocapture = true
  }
}

function disablePostHog(): void {
  if (posthogInstance) {
    posthogInstance.opt_out_capturing()
    posthogInstance.config.disable_session_recording = true
    posthogInstance.config.disable_persistence = true
    posthogInstance.config.autocapture = false
  }
}

interface CSPostHogProviderProps {
  children: ReactNode
}

export function CSPostHogProvider({ children }: CSPostHogProviderProps) {
  useEffect(() => {
    const handleCookieConsentReady = () => {
      if (window.CookieConsent?.acceptedCategory('analytics')) {
        enablePostHog();
      } else {
        disablePostHog();
      }
    };

    // Listen for cookie consent ready event
    window.addEventListener(COOKIE_CONSENT_READY, handleCookieConsentReady);

    // Also check immediately in case cookie consent is already initialized
    if (window.CookieConsent) {
      handleCookieConsentReady();
    }

    return () => {
      window.removeEventListener(COOKIE_CONSENT_READY, handleCookieConsentReady);
    };
  }, []);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
} 